<template>
  <div>
    <h2 class="text-h5 mb-4">
      Клиенты
    </h2>

    <v-alert
      v-if="stageLabel"
      type="info"
      text
      dense
      class="mb-4"
    >
      Показаны идентифицированные клиенты этапа «{{ stageLabel }}».
      Анонимные визиты в список не попадают.
    </v-alert>

    <analytics-filters
      v-model="filters"
      @apply="applyFilters"
    ></analytics-filters>

    <v-row dense class="mb-4">
      <v-col cols="12" sm="6" md="3">
        <v-text-field
          v-model="search"
          label="Поиск (телефон, имя, email)"
          dense
          outlined
          hide-details
          clearable
          @keyup.enter="applyFilters"
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-select
          v-model="stage"
          :items="stageItems"
          label="Этап воронки"
          dense
          outlined
          hide-details
          clearable
        ></v-select>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-text-field
          v-model="content"
          label="Объявление / utm_content"
          dense
          outlined
          hide-details
          clearable
          @keyup.enter="applyFilters"
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6" md="3" class="d-flex align-center">
        <v-btn color="primary" class="mr-2" @click="applyFilters">
          Найти
        </v-btn>
        <v-btn text @click="resetExtra">
          Сбросить доп.
        </v-btn>
      </v-col>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="items"
      :loading="loading"
      :items-per-page="pageSize"
      hide-default-footer
      class="elevation-0 customers-table"
      @click:row="openCustomer"
    >
      <template #item.name="{ item }">
        <div class="d-flex align-center flex-wrap">
          <router-link
            class="text-decoration-none mr-2"
            :to="{ name: 'AnalyticsCustomerDetail', params: { id: item.customer_id } }"
          >
            {{ item.name || 'Без имени' }}
          </router-link>
          <v-chip
            v-if="item.is_new"
            x-small
            outlined
            color="success"
            class="mt-0"
          >
            Новый
          </v-chip>
        </div>
      </template>
      <template #item.first_source="{ item }">
        <span :class="{ 'grey--text': isWeakSource(item.first_source) }">
          {{ item.first_source }}
        </span>
        <div
          v-if="item.first_campaign"
          class="text-caption grey--text"
        >
          {{ item.first_campaign }}
        </div>
      </template>
      <template #item.lead_source="{ item }">
        <span>{{ item.lead_source || '—' }}</span>
        <div
          v-if="item.lead_campaign"
          class="text-caption grey--text"
        >
          {{ item.lead_campaign }}
        </div>
      </template>
      <template #item.revenue="{ item }">
        {{ formatMoney(item.revenue) }} ₽
      </template>
    </v-data-table>
    <div class="d-flex justify-space-between align-center mt-2">
      <span class="text-caption">Всего: {{ total }}</span>
      <div>
        <v-btn
          small
          text
          :disabled="page <= 1"
          @click="load(page - 1)"
        >
          Назад
        </v-btn>
        <span class="mx-2 text-caption">{{ page }}</span>
        <v-btn
          small
          text
          :disabled="page * pageSize >= total"
          @click="load(page + 1)"
        >
          Вперёд
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import Api from '@/api/api'
import AnalyticsFilters from './AnalyticsFilters.vue'

const STAGE_LABELS = {
  visits: 'Визиты',
  form_opens: 'Открытия формы',
  leads: 'Заявки',
  booked: 'Записи',
  came: 'Пришли',
  buyers: 'Купили',
}

function stripEmpty(obj) {
  const out = {}
  Object.keys(obj || {}).forEach(k => {
    const v = obj[k]
    if (v !== '' && v !== null && v !== undefined) out[k] = v
  })
  return out
}

function emptyFilters() {
  return {
    date_from: '',
    date_to: '',
    attribution: 'lead',
    source: '',
    campaign: '',
    company_id: null,
  }
}

export default {
  name: 'AnalyticsClientCard',
  components: { AnalyticsFilters },
  data() {
    return {
      search: '',
      stage: null,
      content: '',
      filters: emptyFilters(),
      items: [],
      total: 0,
      page: 1,
      pageSize: 20,
      loading: false,
      headers: [
        { text: 'Имя', value: 'name' },
        { text: 'Телефон', value: 'phone' },
        { text: 'Email', value: 'email' },
        { text: 'First Touch', value: 'first_source' },
        { text: 'Lead Touch', value: 'lead_source' },
        { text: 'Выручка', value: 'revenue', align: 'end' },
      ],
      stageItems: Object.keys(STAGE_LABELS).map(value => ({
        text: STAGE_LABELS[value],
        value,
      })),
    }
  },
  computed: {
    stageLabel() {
      return STAGE_LABELS[this.stage] || ''
    },
  },
  watch: {
    '$route.query': {
      immediate: true,
      handler(q) {
        this.syncFromRoute(q || {})
        this.load(1)
      },
    },
  },
  methods: {
    isWeakSource(source) {
      return [
        'direct / unknown',
        'direct',
        'internal',
        'payment return',
      ].includes(source)
    },
    formatMoney(v) {
      return Number(v || 0).toLocaleString('ru-RU', {
        maximumFractionDigits: 0,
      })
    },
    syncFromRoute(q) {
      this.search = q.search || ''
      this.stage = q.stage || null
      this.content = q.content || ''
      this.filters = {
        ...emptyFilters(),
        date_from: q.date_from || '',
        date_to: q.date_to || '',
        attribution: q.attribution || 'lead',
        source: q.source || '',
        campaign: q.campaign || '',
        company_id: q.company_id || null,
      }
    },
    buildQuery(extra = {}) {
      return stripEmpty({
        ...this.filters,
        search: this.search,
        stage: this.stage,
        content: this.content,
        ...extra,
      })
    },
    applyFilters(nextFilters) {
      if (nextFilters && typeof nextFilters === 'object') {
        this.filters = { ...emptyFilters(), ...nextFilters }
      }
      this.$router.replace({
        name: 'AnalyticsCustomers',
        query: this.buildQuery(),
      }).catch(() => {})
    },
    resetExtra() {
      this.search = ''
      this.stage = null
      this.content = ''
      this.applyFilters()
    },
    async load(page) {
      this.page = page || 1
      this.loading = true
      try {
        const data = await Api.getAnalyticsCustomers({
          ...this.buildQuery(),
          page: this.page,
          page_size: this.pageSize,
        })
        this.items = data.items || []
        this.total = data.total || 0
      } catch (e) {
        this.items = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    openCustomer(item) {
      if (!item || !item.customer_id) return
      this.$router.push({
        name: 'AnalyticsCustomerDetail',
        params: { id: item.customer_id },
      })
    },
  },
}
</script>

<style scoped>
.customers-table {
  width: 100%;
  overflow-x: auto;
}
.customers-table >>> tbody tr {
  cursor: pointer;
}
</style>
